import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Upload, Button, message, Typography, Divider } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;

const InputModal = ({ visible, onClose, onSubmit, nodeConfig, nodeData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState({});

  const inputFields = nodeData?.config?.inputFields || [];

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const formValues = await form.validateFields();
      
      // Merge form values with file data
      const allData = { ...formValues, ...fileData };
      
      // Transform data to match variable names
      const variableData = {};
      inputFields.forEach(field => {
        if (allData[field.id] !== undefined) {
          variableData[field.variableName] = allData[field.id];
        }
      });

      onSubmit(variableData);
      form.resetFields();
      setFileData({});
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (fieldId, file, fieldConfig) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target.result;
          let processedData;

          if (fieldConfig.inputType === 'csv_upload') {
            // Process CSV data
            const lines = content.split('\n').filter(line => line.trim());
            const headers = lines[0].split(',').map(h => h.trim());
            const rows = lines.slice(1).map(line => {
              const values = line.split(',').map(v => v.trim());
              const row = {};
              headers.forEach((header, index) => {
                row[header] = values[index] || '';
              });
              return row;
            });
            
            processedData = {
              headers,
              rows,
              rawContent: content
            };
          } else {
            // For other file types, store raw content
            processedData = {
              fileName: file.name,
              fileType: file.type,
              content: content
            };
          }

          setFileData(prev => ({
            ...prev,
            [fieldId]: processedData
          }));
          
          message.success(`File "${file.name}" uploaded successfully`);
          resolve(processedData);
        } catch (error) {
          message.error(`Error processing file: ${error.message}`);
          reject(error);
        }
      };

      reader.onerror = () => {
        message.error('Error reading file');
        reject(new Error('Error reading file'));
      };

      reader.readAsText(file);
    });
  };

  const renderInputField = (field) => {
    const commonProps = {
      placeholder: field.placeholder,
      required: field.required
    };

    switch (field.inputType) {
      case 'text':
        return (
          <Input 
            {...commonProps}
            defaultValue={field.defaultValue}
          />
        );

      case 'textarea':
        return (
          <TextArea 
            {...commonProps}
            rows={3}
            defaultValue={field.defaultValue}
          />
        );

      case 'number':
        return (
          <InputNumber 
            {...commonProps}
            style={{ width: '100%' }}
            min={field.validation?.min}
            max={field.validation?.max}
            defaultValue={field.defaultValue ? Number(field.defaultValue) : undefined}
          />
        );

      case 'csv_upload':
      case 'file_upload':
        const acceptedTypes = field.fileConfig?.acceptedTypes || '.csv,.xlsx,.json';
        const maxSize = field.fileConfig?.maxSize || 10;
        
        return (
          <Upload
            beforeUpload={(file) => {
              // Validate file type
              const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
              const acceptedExtensions = acceptedTypes.split(',').map(ext => ext.trim());
              
              if (!acceptedExtensions.includes(fileExtension)) {
                message.error(`File type not allowed. Accepted types: ${acceptedTypes}`);
                return false;
              }
              
              // Validate file size
              if (file.size / 1024 / 1024 > maxSize) {
                message.error(`File too large. Maximum size: ${maxSize}MB`);
                return false;
              }

              handleFileUpload(field.id, file, field);
              return false; // Prevent auto upload
            }}
            showUploadList={false}
            accept={acceptedTypes}
          >
            <Button icon={<UploadOutlined />}>
              {fileData[field.id] ? 
                `✓ ${fileData[field.id].fileName || 'File uploaded'}` : 
                'Click to Upload'
              }
            </Button>
          </Upload>
        );

      default:
        return <Input {...commonProps} />;
    }
  };

  return (
    <Modal
      title={
        <div>
          <span>📝 Input Required</span>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
            {nodeData?.config?.description || 'Please provide the following inputs to continue'}
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      maskClosable={false}
      okText="Continue Workflow"
      cancelText="Cancel"
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 16 }}
      >
        {inputFields.map((field, index) => (
          <div key={field.id}>
            <Form.Item
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{field.label}</span>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Variable: {field.variableName}
                  </Text>
                </div>
              }
              name={field.id}
              rules={[
                { 
                  required: field.required, 
                  message: `${field.label} is required` 
                },
                ...(field.inputType === 'number' && field.validation?.min ? [
                  { 
                    type: 'number', 
                    min: field.validation.min, 
                    message: `Must be at least ${field.validation.min}` 
                  }
                ] : []),
                ...(field.inputType === 'number' && field.validation?.max ? [
                  { 
                    type: 'number', 
                    max: field.validation.max, 
                    message: `Must be at most ${field.validation.max}` 
                  }
                ] : [])
              ]}
            >
              {renderInputField(field)}
            </Form.Item>
            
            {index < inputFields.length - 1 && <Divider style={{ margin: '12px 0' }} />}
          </div>
        ))}
      </Form>

      {inputFields.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          <Text type="secondary">No input fields configured for this node</Text>
        </div>
      )}
    </Modal>
  );
};

export default InputModal;